import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Divider, Header, Segment } from "semantic-ui-react";
import RetargetingSegmentsService from "../../../services/retargeting-segments";
import AppNotification from "../../common/notification-banner/app-notification";
import RetargetingSegmentsForm from "../form";

const backUrl = "/retargeting-segments";
const service = new RetargetingSegmentsService();

const RetargetingSegmentEditPage = ({ history, match }) => {
  const intl = useIntl();
  const segmentId = Number.parseInt(match.params.segmentId, 10) || 0;
  const [serverError, setServerError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * navigate user back
   */
  const navigateToGrid = useCallback(() => {
    history.push(backUrl);
  }, [history]);

  const transformSegmentData = useCallback(
    (data) => ({
      ...data,
      sku_ids: data.sku_ids.join(", "),
      attribution_level: data.sku_ids.length ? "sku" : "brand",
    }),
    [],
  );

  const fetchRetargetingSegment = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await service.getRetargetingSegment(segmentId);
      setInitialData(transformSegmentData(response.data));
    } catch (err) {
      setServerError(err.error);
    } finally {
      setIsLoading(false);
    }
  }, [segmentId, transformSegmentData]);

  useEffect(() => {
    fetchRetargetingSegment();
  }, [fetchRetargetingSegment]);

  const handleUpdateRetargetingSegment = async (values) => {
    setIsSubmitting(true);
    try {
      const { name } = values;
      await service.updateRetargetingSegment(segmentId, {
        name,
      });
      history.push(backUrl, { action: "updated" });
    } catch (e) {
      setServerError(e?.error?.message || e?.message || "An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!segmentId) {
    navigateToGrid();
    return <></>;
  }

  return (
    <Segment basic>
      <AppNotification
        isError
        error={serverError}
        onDismiss={() => setServerError(null)}
      >
        {serverError}
      </AppNotification>
      <Header as="h2">
        {intl.formatMessage({
          id: "HEADING_EDIT_BRAND_SKU_SEGMENT",
          defaultMessage: "Edit Brand and SKU Segment",
        })}
      </Header>
      <Divider />
      <Segment
        basic
        loading={isLoading}
      >
        {initialData && (
          <RetargetingSegmentsForm
            formType="edit"
            isSubmitting={isSubmitting}
            initialData={initialData}
            onCancel={navigateToGrid}
            onSubmit={handleUpdateRetargetingSegment}
            submitText={intl.formatMessage({
              id: "BTN_SAVE_CHANGES",
              defaultMessage: "Save Changes",
            })}
          />
        )}
      </Segment>
    </Segment>
  );
};

export default RetargetingSegmentEditPage;

RetargetingSegmentEditPage.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};
